import { useMemo } from "react";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { LineChart as LineIcon } from "lucide-react";

const NAVY = "#1B2B4B";
const GREEN = "#35622F";

function TooltipCard({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  const d = new Date(row.date + "T00:00:00Z");
  const label = d.toLocaleDateString("en-PH", {
    month: "short",
    day: "numeric",
  });
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-md">
      <div className="font-semibold text-gray-900">{label}</div>
      <div className="mt-0.5 text-gray-500">
        <span
          className="font-bold text-brand-navy tabular-nums"
          data-testid="growth-tooltip-count"
        >
          {row.count}
        </span>{" "}
        new sign-up{row.count === 1 ? "" : "s"}
      </div>
    </div>
  );
}

export function GrowthMiniChart({ series }) {
  const total = useMemo(
    () => (series || []).reduce((sum, r) => sum + (r.count || 0), 0),
    [series]
  );
  const days = series?.length || 0;
  const first = series?.[0]?.date;
  const last = series?.[series.length - 1]?.date;
  const rangeLabel =
    first && last
      ? `${new Date(first + "T00:00:00Z").toLocaleDateString("en-PH", {
          month: "short",
          day: "numeric",
        })} — ${new Date(last + "T00:00:00Z").toLocaleDateString("en-PH", {
          month: "short",
          day: "numeric",
        })}`
      : "";

  return (
    <div
      className="rounded-2xl border border-gray-100 bg-white p-6 shadow-mb-card"
      data-testid="admin-growth-chart"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-1.5 rounded-full border border-brand-navy/15 bg-brand-navy/5 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-brand-navy">
            <LineIcon className="h-3 w-3" />
            Growth · last {days} days
          </div>
          <h3 className="mt-3 text-xl font-bold tracking-tight text-gray-900">
            New broker sign-ups
          </h3>
          <div className="mt-1 text-xs text-gray-500">{rangeLabel}</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
            Period total
          </div>
          <div
            className="mt-1 text-3xl font-bold text-brand-navy tabular-nums"
            data-testid="growth-total-value"
          >
            {total.toLocaleString("en-PH")}
          </div>
        </div>
      </div>

      <div className="mt-6 h-52 w-full" data-testid="admin-growth-chart-canvas">
        <ResponsiveContainer width="100%" height="100%" minHeight={208} debounce={80}>
          <AreaChart
            data={series || []}
            margin={{ top: 8, right: 6, left: -20, bottom: 0 }}
          >
            <defs>
              <linearGradient id="mbGrowthFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={GREEN} stopOpacity={0.22} />
                <stop offset="100%" stopColor={GREEN} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tick={{ fill: "#94a3b8", fontSize: 10 }}
              interval={4}
              tickFormatter={(d) =>
                new Date(d + "T00:00:00Z").toLocaleDateString("en-PH", {
                  month: "short",
                  day: "numeric",
                })
              }
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              width={30}
              tick={{ fill: "#94a3b8", fontSize: 10 }}
              allowDecimals={false}
            />
            <Tooltip
              cursor={{ stroke: "#e5e7eb", strokeWidth: 1 }}
              content={<TooltipCard />}
            />
            <Area
              type="monotone"
              dataKey="count"
              stroke={NAVY}
              strokeWidth={2}
              fill="url(#mbGrowthFill)"
              dot={false}
              activeDot={{ r: 4, stroke: NAVY, strokeWidth: 2, fill: "#fff" }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
